import './styles/CalculatorInputs.css';

interface CalculatorInputsProps {
  state: string;
  monthlyBill: string;
  offsetPercent: string;
  systemLife: string;
  overrideSize: string;
  isFinanced: boolean;
  pricePerWatt: string;
  utilityRate: string;
  onStateChange: (value: string) => void;
  onMonthlyBillChange: (value: string) => void;
  onOffsetPercentChange: (value: string) => void;
  onSystemLifeChange: (value: string) => void;
  onOverrideSizeChange: (value: string) => void;
  onFinancedChange: (value: boolean) => void;
  onPricePerWattChange: (value: string) => void;
  onUtilityRateChange: (value: string) => void;
  avgCostPerKWByState: Record<string, number>;
}

export default function CalculatorInputs({
  state,
  monthlyBill,
  offsetPercent,
  systemLife,
  overrideSize,
  isFinanced,
  pricePerWatt,
  utilityRate,
  onStateChange,
  onMonthlyBillChange,
  onOffsetPercentChange,
  onSystemLifeChange,
  onOverrideSizeChange,
  onFinancedChange,
  onPricePerWattChange,
  onUtilityRateChange,
  avgCostPerKWByState,
}: CalculatorInputsProps) {
  return (
    <div className="calculator-section">
      <h2>Your Details</h2>
      <p className="calc-inputs-intro">
        Two numbers are all we need — tweak the advanced stuff only if you
        want to.
      </p>

      <div className="form-group">
        <label className="solar-form-label">
          Your State:
          <select
            value={state}
            onChange={(e) => onStateChange(e.target.value)}
            className="solar-form-input"
          >
            {Object.keys(avgCostPerKWByState).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="form-group">
        <label className="solar-form-label">
          Monthly Electric Bill ($):
          <input
            type="number"
            value={monthlyBill}
            onChange={(e) => onMonthlyBillChange(e.target.value)}
            className="solar-form-input"
            placeholder="e.g. 180"
            min="0"
            step="1"
          />
          <span className="input-hint">
            Grab any recent bill — the monthly total is all you need.
          </span>
        </label>
      </div>

      <div className="form-group">
        <label className="solar-form-label checkbox-label">
          <input
            type="checkbox"
            checked={isFinanced}
            onChange={(e) => onFinancedChange(e.target.checked)}
          />
          I'd finance it (est. 4.99% APR, 20 yr)
        </label>
      </div>

      <details className="advanced-options">
        <summary>Advanced options</summary>

        <div className="form-group">
          <label className="solar-form-label">
            Offset Goal (%):
            <input
              type="number"
              value={offsetPercent}
              onChange={(e) => onOffsetPercentChange(e.target.value)}
              className="solar-form-input"
              min="0"
              max="100"
              step="1"
            />
            <span className="input-hint">
              How much of your usage the panels should cover. 80–100% is
              typical; more than 100% usually isn't worth it.
            </span>
          </label>
        </div>

        <div className="form-group">
          <label className="solar-form-label">
            Utility Rate ($/kWh):
            <input
              type="number"
              value={utilityRate}
              onChange={(e) => onUtilityRateChange(e.target.value)}
              className="solar-form-input"
              min="0.05"
              max="0.60"
              step="0.01"
            />
            <span className="input-hint">
              What you pay per kWh — it's on your bill. Florida averages
              ~$0.14 in 2026.
            </span>
          </label>
        </div>

        <div className="form-group">
          <label className="solar-form-label">
            Price Per Watt ($):
            <input
              type="number"
              value={pricePerWatt}
              onChange={(e) => onPricePerWattChange(e.target.value)}
              className="solar-form-input"
              min="0"
              step="0.01"
            />
            <span className="input-hint">
              Installed cost. Good Florida quotes land around $2.50–$3.00 —
              always compare against the cash price.
            </span>
          </label>
        </div>

        <div className="form-group">
          <label className="solar-form-label">
            System Lifetime (years):
            <input
              type="number"
              value={systemLife}
              onChange={(e) => onSystemLifeChange(e.target.value)}
              className="solar-form-input"
              min="1"
              max="30"
              step="1"
            />
          </label>
        </div>

        <div className="form-group">
          <label className="solar-form-label">
            Override System Size (kW):
            <input
              type="number"
              value={overrideSize}
              onChange={(e) => onOverrideSizeChange(e.target.value)}
              className="solar-form-input"
              placeholder="Leave blank to auto-size"
              min="0"
              step="0.1"
            />
            <span className="input-hint">
              Only if you already have a quote for a specific size.
            </span>
          </label>
        </div>
      </details>
    </div>
  );
}
